import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MediaGallery from "./MediaGallery";
import { useMediaScope } from "../context/PremiseScopeContext";
import type { AssetSummary, MediaLayer } from "../http/foyer";
import type { ScreenOrientation } from "@shared/media";

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, asset: AssetSummary) => void;
  /** Offer only files that fit a list of this orientation (UNIT-1). */
  fitsOrientation?: ScreenOrientation;
}

/**
 * Choose an image or clip from a layer this scope actually owns.
 *
 * Global media is NOT offered here when a location or customer is in scope.
 * It is Nenda's own shelf - almost entirely app and radio catalogue icons -
 * and a hotel picking a file out of it would be building its signage from
 * assets it does not control and cannot manage. The tab only appears where
 * there is nothing else in scope, which is the admin's own Global media page.
 */
export default function AssetsPicker({
  open,
  onClose,
  onSelect,
  fitsOrientation,
}: Props) {
  const { t } = useTranslation(["content", "common"]);
  const scope = useMediaScope();

  const tabs = useMemo(() => {
    const out: Array<{ key: string; label: string; layer: MediaLayer }> = [];
    if (scope.premiseId) {
      out.push({
        key: "premise",
        label: t("assetsPicker.tabs.premise"),
        layer: { kind: "premise", premiseId: scope.premiseId },
      });
    }
    if (scope.companyId) {
      out.push({
        key: "company",
        label: t("assetsPicker.tabs.company"),
        layer: { kind: "company", companyId: scope.companyId },
      });
    }
    // Only when nothing else is in scope - see the note above.
    if (out.length === 0) {
      out.push({
        key: "global",
        label: t("assetsPicker.tabs.global"),
        layer: { kind: "global" },
      });
    }
    return out;
  }, [scope.premiseId, scope.companyId, t]);

  const [active, setActive] = useState(0);
  // A scope change can shrink the tab list under a stale index.
  const index = Math.min(active, tabs.length - 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="assets-picker"
    >
      <DialogTitle sx={{ pb: 0 }}>
        {t("assetsPicker.title")}
        {fitsOrientation && (
          <Typography
            variant="meta"
            component="span"
            data-testid="picker-orientation-note"
            sx={{ display: "block", color: "text.secondary" }}
          >
            {t("assetsPicker.orientationNote", {
              orientation: t(`assetsPicker.orientation.${fitsOrientation}`),
            })}
          </Typography>
        )}
      </DialogTitle>
      {tabs.length > 1 && (
        <Tabs
          value={index}
          onChange={(_, v: number) => setActive(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
          data-testid="picker-layer-tabs"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              data-testid={`picker-tab-${tab.key}`}
            />
          ))}
        </Tabs>
      )}
      <DialogContent dividers>
        <MediaGallery
          // Remount on tab change so folder navigation starts at that layer's
          // root rather than inside a path the new layer may not even have.
          key={tabs[index]!.key}
          layer={tabs[index]!.layer}
          scrollContain
          fitsOrientation={fitsOrientation}
          onSelect={(url, asset) => {
            onSelect(url, asset);
            onClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
      </DialogActions>
    </Dialog>
  );
}
